'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  useIntl,
  FormattedMessage,
  defineMessage,
  MessageDescriptor,
} from 'react-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { A, H3, P } from '@osmosis/tailwind-ui/components/Text';
import {
  bg0,
  bg1,
  bgPrimaryLightest,
} from '@osmosis/tailwind-ui/styles/background';
import { borderNeutral300 } from '@osmosis/tailwind-ui/styles/border';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  text4,
  text5,
  textBlue,
  textOrange700,
} from '@osmosis/tailwind-ui/styles/text';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { getAuthPath } from '../../lib/composer/url';
import { Route } from '../../hooks/useRouterPush';
import {
  EXAMPLE_PROMPTS,
  LOCKED_VALUE_ITEMS,
  PromptId,
} from '../../lib/util/examplePrompts';
import { TrustStrip } from './TrustStrip';
import { track } from '../../lib/util/tracking';
import { useAIPanelContext } from '../../context/AIPanelContext';
import TrialCTA from '../TrialCTA';
import type { TrialCtaArgs } from '../../lib/composer/schemas/answersValueProp';

/**
 * Logged-out marketing titles that intentionally differ from the in-app chip
 * labels in `EXAMPLE_PROMPTS`. Anything not listed falls back to `prompt.label`.
 */
const LOGGED_OUT_TITLE_OVERRIDES: Partial<Record<PromptId, MessageDescriptor>> =
  {
    questions: defineMessage({
      id: 'ai-panel.logged-out.card.questions.title',
      defaultMessage: 'Quiz me with 3 questions',
    }),
  };

/**
 * Static, non-interactive promo body for logged-out or expired users.
 */
const LoggedOutAIPanelContent = ({ trialCta }: { trialCta?: TrialCtaArgs }) => {
  const [signUpLoading, setSignUpLoading] = useState(false);

  const { isOpen } = useAIPanelContext();

  const { formatMessage } = useIntl();
  const { getPlansUrl } = useUserContext();
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const plansUrl = getPlansUrl();

  const trackLoggedOutRef = useRef(false);

  const handleTrialClick = () => {
    track('begin_registration', { source: 'ai lockout panel' }, ['gtm']);
    setSignUpLoading(true);
  };

  useEffect(() => {
    if (trackLoggedOutRef.current || !isOpen) return;
    trackLoggedOutRef.current = true;
    track(
      'osmosis-ai-lockout',
      {
        page: pathName,
        source: 'logged-out-ai-panel',
      },
      ['keen', 'pendo'],
    );
  }, [isOpen]);

  return (
    <Flex
      className="flex-1 flex-col gap-2 overflow-y-auto pt-2 pb-2 items-center"
      data-testid="logged-out-ai-panel-body"
    >
      <TrustStrip classNames={{ root: 'w-full' }} />

      {/* Example prompt cards — reuse shared prompt icons/text; non-interactive, route to sign-up */}
      <Flex className="flex-col gap-3 w-full px-4">
        {EXAMPLE_PROMPTS.map((prompt) => (
          <Flex
            key={prompt.id}
            className={`items-start gap-3 rounded-lg border p-2 ${borderNeutral300}`}
            data-testid={`logged-out-ai-prompt-card-${prompt.id}`}
          >
            <Icon
              icon={prompt.icon.icon}
              size={18}
              className={cn(prompt.icon.color, 'shrink-0', 'mt-1')}
            />
            <Box className="min-w-0 flex-1">
              <P className={`text-sm font-semibold ${text5}`}>
                <FormattedMessage
                  {...(LOGGED_OUT_TITLE_OVERRIDES[prompt.id] ?? prompt.label)}
                />
              </P>
              <P className={`!text-xs ${text4}`}>
                <FormattedMessage {...prompt.description} />
              </P>
            </Box>
            <Flex
              className={`shrink-0 items-center self-center gap-1 rounded-full px-2 py-1 ${bgPrimaryLightest}`}
            >
              <Icon icon={faLockKeyhole} size={10} className={textBlue} />
              <P className={`!text-sm font-medium ${textBlue}`}>
                {formatMessage({
                  id: 'ai-panel.logged-out.trial-pill',
                  defaultMessage: 'Trial',
                })}
              </P>
            </Flex>
          </Flex>
        ))}
      </Flex>

      {/* Value proposition card */}
      <Flex className="w-full px-4">
        <Box className={`rounded-lg p-4  w-full ${bg1}`}>
          <Flex className="items-center gap-2">
            <Icon icon={faCrown} size={16} className={textOrange700} />
            <H3 className={`!text-base font-semibold ${text5}`}>
              {formatMessage({
                id: 'ai-panel.logged-out.value.heading',
                defaultMessage: 'Try the full Osmosis experience',
              })}
            </H3>
          </Flex>
          <Flex className="mt-3 mb-4 ml-5 flex-col gap-2">
            {LOCKED_VALUE_ITEMS.map((item) => (
              <Flex key={item.key} className="items-center gap-2">
                <Icon icon={faCheck} size={16} className={textBlue} />
                <P className={`text-sm ${text5}`}>
                  {formatMessage({
                    id: item.id,
                    defaultMessage: item.defaultMessage,
                  })}
                </P>
              </Flex>
            ))}
          </Flex>
          {/* Calls to action */}
          <Flex className="flex-col items-center gap-3">
            {trialCta !== undefined ? (
              <TrialCTA
                copy={trialCta}
                isLoading={signUpLoading}
                onButtonClick={handleTrialClick}
                containerTestId="logged-out-ai-trial-cta"
                buttonTestId="logged-out-ai-cta-start-trial"
                fineprintTestId="logged-out-ai-trial-cta-fineprint"
                classNames={{
                  container: 'w-full max-w-xs rounded-lg',
                  button: 'w-full',
                }}
              />
            ) : (
              <A
                tabIndex={-1}
                href={getAuthPath(Route.CREATE, pathName, searchParams)}
                className="no-underline"
              >
                <Button
                  variant="outline"
                  color="blue"
                  className={cn('px-6 py-2', bg0)}
                  data-testid="logged-out-ai-cta-start-trial"
                  isLoading={signUpLoading}
                  onClick={handleTrialClick}
                >
                  <FormattedMessage
                    id="plans.cta.button"
                    defaultMessage="Start free trial"
                  />
                </Button>
              </A>
            )}
            <A
              href={plansUrl}
              className={`text-sm font-medium ${textBlue} no-underline p-2`}
            >
              {formatMessage({
                id: 'learn.lockout_block.button_text.see_plans',
                defaultMessage: 'See plans',
              })}
            </A>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

export default LoggedOutAIPanelContent;
